// components/DonationCalendar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import CustomButton from '../general/button';
import { useNavigate } from 'react-router-dom';
import { setDailyDonationStatus } from '../../utils/setDailyDonationStatus';
import ShowCurrencies from '../../utils/showCurrencies';

const DonationCalendar = ({
  donationSchedule = [],
  currency = 'USD',
  startDate,
  timeZone,
  totalAmount = 0
}) => {
  const navigate = useNavigate();
  if (!startDate) return null;

  const startDT =
    typeof startDate === 'string'
      ? DateTime.fromISO(startDate, { zone: timeZone }).startOf('day')
      : DateTime.fromJSDate(startDate, { zone: timeZone }).startOf('day');

  // Always show 10-day window from startDate
  const windowEnd = startDT.plus({ days: 9 });

  // Filter schedule to 10 days starting from startDate
  const scheduleToShow = donationSchedule.filter(item => {
    const d = DateTime.fromISO(item.date, { zone: timeZone }).startOf('day');
    return d >= startDT && d <= windowEnd;
  });

  // Determine intended start date (first date in full donationSchedule)
  const fullStartDT = startDT;


  // Annotate days with correct day number based on full donation range
  const annotated = scheduleToShow.map(day => {
    const d = DateTime.fromISO(day.date, { zone: timeZone }).startOf('day');
    const offset = Math.floor(d.diff(fullStartDT, 'days').days);
    return { ...day, dayNumber: offset + 1 };
  });

  const grouped = annotated.reduce((acc, day) => {
    const monthYear = DateTime.fromISO(day.date, { zone: timeZone }).toFormat('LLLL yyyy');
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(day);
    return acc;
  }, {});

  const MINIMUM = currency.toLowerCase() === 'myr' ? 200 : 50;
  const isMet = totalAmount >= MINIMUM;

  return (
    <div className="w-full bg-[#DFEEF2] p-4 md:p-8 rounded-md">
      <h2 className="text-[#253B7E] text-lg md:text-2xl mb-4 font-bold">
        Your Donation Calendar
      </h2>

      {Object.entries(grouped).map(([month, days]) => (
        <div key={month} className="mb-6">
          <h3 className="text-black text-md md:text-xl mb-3">{month}</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-9 gap-2">
            {days.map(day => (
              <div key={day.date} className={`border border-stone-800 p-1 md:p-2 rounded text-center transition-all duration-200 ease-in-out hover:bg-[#c1e7f1] ${day.isMultiplierNight ? 'bg-yellow-100 border-red-500' : ''}`}>
                <p className="text-base md:text-lg font-bold text-[#253B7E]">
                  Day {day.dayNumber}
                </p>
                <p className="text-xs text-[#797979]">
                  {DateTime.fromISO(day.date, { zone: timeZone }).toFormat('LLLL d, yyyy')}
                </p>
                <p className="text-sm text-[#253B7E] my-1">
                  {DateTime.fromISO(day.date, { zone: timeZone }).toFormat('cccc')}
                </p>
                <div className="w-full rounded bg-[#253B7E] text-white text-center py-1 relative">
                  <div className="text-base md:text-lg font-semibold">
                    {ShowCurrencies(currency, day.amount)}
                  </div>
                  {day.isMultiplierNight && (
                    <span className="absolute top-[-8px] right-[-8px] bg-red-600 text-white text-[10px] font-semibold px-2 py-[2px] rounded-full shadow">
                      x{day.shares}
                    </span>
                  )}
                </div>
              </div>
            ))}

          </div>
        </div>
      ))}

      {annotated.length > 0 && (
        <div className="mt-4">
          <CustomButton
            title="Schedule your donation"
            className={`w-full sm:w-auto`}
            disabled={!isMet}
            onClick={() => {
              if (!isMet) return;
              setDailyDonationStatus();
              localStorage.setItem('RECURRING_DONATION_TYPE', JSON.stringify('dhul-hijjah'));
              navigate('/checkout', { state: { from: '10-steps-to-jannah' } });
            }}
          />
        </div>
      )}
    </div>
  );
};

DonationCalendar.propTypes = {
  donationSchedule: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      isMultiplierNight: PropTypes.bool
    })
  ).isRequired,
  currency: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  timeZone: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
};

export default DonationCalendar;
